/**
 * The URL for a settings tab.
 *
 * The settings page derives its active tab from `location.pathname`, so a
 * link must put the tab in the path. Seven call sites hand-wrote
 * `/settings?tab=billing` instead, which is a valid URL that renders the
 * **Profile** tab.
 *
 * That was not only an onboarding problem. Five of the seven are the
 * billing status banner's calls to action — payment failed, card expiring,
 * trial ending. A customer clicking "Update payment method" landed on
 * their profile page with no explanation. And because the checkout
 * auto-start lives in an effect inside `BillingSettings`, which only mounts
 * on the billing tab, the upgrade parameter carried along with those links
 * did nothing at all: the free-plan signup funnel completed to a page that
 * could not start the checkout it was sent to start.
 *
 * Exists so nobody hand-writes the URL again. The tab union is duplicated
 * from the settings page rather than imported to avoid a page-level import
 * in components that only need a link; adding a tab in one place and not
 * the other is a compile error at the call site, which is the failure mode
 * worth having.
 */
export type SettingsTabId =
  | "profile"
  | "general"
  | "team"
  | "billing"
  | "notifications"
  | "security"
  | "sso"
  | "integrations";

export function settingsPath(
  tab: SettingsTabId,
  params?: Record<string, string | undefined>
): string {
  // "profile" is the bare /settings route; "general" lives under
  // /settings/organization. Both match what `setActiveTab` navigates to.
  const base =
    tab === "profile"
      ? "/settings"
      : tab === "general"
        ? "/settings/organization"
        : `/settings/${tab}`;

  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined) query.set(key, value);
  }
  const suffix = query.toString();
  return suffix ? `${base}?${suffix}` : base;
}
